import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { useState } from 'react'
import { LockedPadlockOMIcon, UnlockedPadlockOMIcon } from '@omnea-app/icons'
import { ConfirmDialog } from '@omnea-app/omui/ConfirmDialog'
import { recordAuthorization, type RecordVerb } from './accessRules'
import { noRole } from './roleAuthorization'
import { useIsPermitted } from './useIsPermitted'
import { useUpdateRecordConfidentiality } from './useUpdateRecordConfidentiality'
import type { ClearanceEntity } from '@omnea-digital/authz-model'
import type { QueryKey } from '@tanstack/react-query'

type ConfidentialityKey =
  | 'confirm-title'
  | 'confirm-description'
  | 'confirm-self-lockout-warning'
  | 'confirm-mark'
  | 'confirm-cancel'
  | 'unmark-confirm-title'
  | 'unmark-confirm-description'
  | 'unmark-confirm-remove'
  | 'update-failed'

type Props<Entity extends ClearanceEntity> = {
  entity: Entity
  recordId: string
  isConfidential: boolean
  permission: RecordVerb<Entity>
  invalidateKeys: QueryKey[]
  translateCopy: (key: ConfidentialityKey) => string
  labels: { mark: string; unmark: string }
  /** Called when the confirmation closes, never on click: a menu that unmounts its items on close would take the open dialog with it. */
  onClose?: () => void
}

export const ConfidentialRecordMenuItem = <Entity extends ClearanceEntity>({
  entity,
  recordId,
  isConfidential,
  permission,
  invalidateKeys,
  translateCopy: t,
  labels,
  onClose,
}: Props<Entity>) => {
  const isPermittedToMark = useIsPermitted({ authorization: recordAuthorization(entity, permission, noRole) }, recordId)
  const { mutate, isPending } = useUpdateRecordConfidentiality({
    entity,
    recordId,
    invalidateKeys,
    errorMessage: t('update-failed'),
  })
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  if (!isPermittedToMark) return null

  const closeDialog = () => {
    setIsDialogOpen(false)
    onClose?.()
  }

  return (
    <>
      {/* Disabled while the flip is in flight, so reopening the menu against the stale flag cannot queue a second submit. */}
      <MenuItem onClick={() => setIsDialogOpen(true)} disabled={isPending} data-testid={`confidential-${entity}-menu-item`}>
        <ListItemIcon>{isConfidential ? <UnlockedPadlockOMIcon /> : <LockedPadlockOMIcon />}</ListItemIcon>
        <ListItemText>{isConfidential ? labels.unmark : labels.mark}</ListItemText>
      </MenuItem>
      <ConfirmDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onConfirm={() => mutate(!isConfidential)}
        title={isConfidential ? t('unmark-confirm-title') : t('confirm-title')}
        bodyTexts={
          isConfidential ? [t('unmark-confirm-description')] : [t('confirm-description'), t('confirm-self-lockout-warning')]
        }
        actions={[
          { variant: 'contained', color: 'secondary', text: t('confirm-cancel'), action: 'cancel' },
          {
            variant: 'contained',
            color: 'error',
            text: isConfidential ? t('unmark-confirm-remove') : t('confirm-mark'),
            action: 'confirm',
          },
        ]}
      />
    </>
  )
}
